<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog supplier-merge-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="supplier-merge-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="supplier-merge-modal-title">{{ text.detail.mergeModal.title }}</h3>
          <p>{{ text.detail.mergeModal.subtitle }}</p>
        </div>

        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.detail.mergeModal.closeAriaLabel"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__content supplier-merge-modal__content">
        <form class="app-modal__form" @submit.prevent="emitSubmit">
          <section class="app-modal__section">
            <div class="app-modal__grid two-columns supplier-merge-modal__top-row">
              <div class="app-modal-field">
                <span>{{ text.detail.mergeModal.primarySupplierLabel }}</span>
                <div class="supplier-merge-modal__readonly">{{ primarySupplierName }}</div>
              </div>

              <div class="app-modal-field">
                <span>{{ text.detail.mergeModal.mergeWithLabel }}</span>
                <multiselect
                  v-model="selectedMergeSupplier"
                  :options="mergeSupplierOptions"
                  track-by="id"
                  label="name"
                  :show-labels="false"
                  :searchable="true"
                  :allow-empty="true"
                  :loading="loadingCandidates"
                  :placeholder="text.detail.mergeModal.mergeWithPlaceholder"
                  class="app-modal-select"
                >
                  <template #noResult>
                    <span>{{ text.detail.mergeModal.noOptions }}</span>
                  </template>
                  <template #noOptions>
                    <span>{{ text.detail.mergeModal.noOptions }}</span>
                  </template>
                </multiselect>
              </div>
            </div>

            <div class="supplier-merge-modal__separator">
              <h4>{{ text.detail.mergeModal.infoSectionTitle }}</h4>
            </div>

            <div class="supplier-merge-modal__table-wrap" v-if="showInformationTable">
              <table class="supplier-merge-modal__table">
                <thead>
                  <tr>
                    <th>{{ text.detail.mergeModal.tableHeaders.field }}</th>
                    <th>{{ primarySupplierName }}</th>
                    <th>{{ mergeSupplierName }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in informationRows" :key="row.key">
                    <td>{{ row.label }}</td>
                    <td>
                      <label class="supplier-merge-modal__choice">
                        <span
                          v-if="hasValue(row.primaryValue)"
                          class="supplier-merge-modal__checkbox"
                          :class="{ 'is-checked': row.source === 'primary' }"
                          role="button"
                          tabindex="0"
                          @click="selectInformationSource(row.key, 'primary')"
                          @keydown.enter.prevent="selectInformationSource(row.key, 'primary')"
                        >
                          <i class="fa fa-check" aria-hidden="true"></i>
                        </span>
                        <span v-else class="supplier-merge-modal__checkbox supplier-merge-modal__checkbox--hidden"></span>
                        <span>{{ row.primaryValue }}</span>
                      </label>
                    </td>
                    <td>
                      <label class="supplier-merge-modal__choice">
                        <span
                          v-if="hasValue(row.mergeValue)"
                          class="supplier-merge-modal__checkbox"
                          :class="{ 'is-checked': row.source === 'merge' }"
                          role="button"
                          tabindex="0"
                          @click="selectInformationSource(row.key, 'merge')"
                          @keydown.enter.prevent="selectInformationSource(row.key, 'merge')"
                        >
                          <i class="fa fa-check" aria-hidden="true"></i>
                        </span>
                        <span v-else class="supplier-merge-modal__checkbox supplier-merge-modal__checkbox--hidden"></span>
                        <span>{{ row.mergeValue }}</span>
                      </label>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="supplier-merge-modal__separator">
              <h4>{{ text.detail.mergeModal.billingSectionTitle }}</h4>
            </div>

            <div class="supplier-merge-modal__warning supplier-merge-modal__warning--soft">
              <i class="fa fa-info-circle" aria-hidden="true"></i>
              <p>{{ text.detail.mergeModal.billingWarning }}</p>
            </div>

            <div class="supplier-merge-modal__list" v-if="showMergeDataSections">
              <label
                v-for="row in billingRows"
                :key="`billing-${row.key}`"
                class="supplier-merge-modal__list-item"
                :class="{ 'supplier-merge-modal__list-item--locked': row.locked }"
              >
                <span
                  class="supplier-merge-modal__checkbox"
                  :class="{ 'is-checked': row.checked, 'is-disabled': row.locked }"
                  role="button"
                  tabindex="0"
                  @click="toggleBillingRow(row.key)"
                  @keydown.enter.prevent="toggleBillingRow(row.key)"
                >
                  <i class="fa fa-check" aria-hidden="true"></i>
                </span>
                <span class="supplier-merge-modal__list-main">{{ row.label }}</span>
                <small>{{ row.originLabel }}</small>
              </label>
            </div>

            <div class="supplier-merge-modal__separator">
              <h4>{{ text.detail.mergeModal.contactsSectionTitle }}</h4>
            </div>

            <div class="supplier-merge-modal__list" v-if="showMergeDataSections">
              <label
                v-for="row in contactsRows"
                :key="`contact-${row.key}`"
                class="supplier-merge-modal__list-item"
              >
                <span
                  class="supplier-merge-modal__checkbox"
                  :class="{ 'is-checked': row.checked }"
                  role="button"
                  tabindex="0"
                  @click="toggleContactRow(row.key)"
                  @keydown.enter.prevent="toggleContactRow(row.key)"
                >
                  <i class="fa fa-check" aria-hidden="true"></i>
                </span>
                <span class="supplier-merge-modal__list-main">{{ row.label }}</span>
                <small>{{ row.originLabel }}</small>
              </label>
            </div>

            <div class="supplier-merge-modal__separator">
              <h4>{{ text.detail.mergeModal.relatedSectionTitle }}</h4>
            </div>

            <table class="supplier-merge-modal__table supplier-merge-modal__table--summary" v-if="showMergeDataSections">
              <thead>
                <tr>
                  <th></th>
                  <th>{{ primarySupplierName }}</th>
                  <th>{{ mergeSupplierName }}</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="metric in relatedSummaryRows" :key="metric.key">
                  <td>{{ metric.label }}</td>
                  <td>{{ metric.primary }}</td>
                  <td>{{ metric.merge }}</td>
                  <td>{{ metric.total }}</td>
                </tr>
              </tbody>
            </table>

            <div class="supplier-merge-modal__separator supplier-merge-modal__separator--warning"></div>

            <div class="supplier-merge-modal__warning supplier-merge-modal__warning--info">
              <i class="fa fa-info-circle" aria-hidden="true"></i>
              <p>{{ text.detail.mergeModal.irreversibleWarning }}</p>
            </div>

            <div class="app-modal-field supplier-merge-modal__confirm">
              <span>{{ text.detail.mergeModal.confirmLabel }}</span>
              <input
                v-model="confirmationValue"
                type="text"
                class="form-control"
                :placeholder="text.detail.mergeModal.confirmPlaceholder"
              >
            </div>
          </section>

          <footer class="app-modal__footer">
            <div class="app-modal__footer-right">
              <button type="button" class="btn btn-light" :disabled="submitting" @click="emitClose">
                {{ text.detail.mergeModal.actions.cancel }}
              </button>
              <button type="submit" class="btn btn-primary" :disabled="!canSubmitMerge || submitting">
                {{ submitting ? text.detail.mergeModal.actions.submitting : text.detail.mergeModal.actions.submit }}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </section>
  </div>
</template>

<script>
import suppliersService from '@/services/suppliers';
import expensesService from '@/services/expenses';
import feesService from '@/services/fees';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('suppliers');

const toTextValue = (value) => String(value ?? '').trim();
const hasContent = (value) => toTextValue(value) !== '';

const buildBillingLabel = (row = {}) => {
  const name = toTextValue(row?.name || row?.business_name || row?.fiscal_name);
  const rfc = toTextValue(row?.rfc);

  if (name !== '' && rfc !== '') {
    return `${name} (${rfc})`;
  }

  return name || rfc || 'Dato de facturación';
};

const buildContactLabel = (row = {}) => {
  const fullName = toTextValue(row?.name || `${row?.first_name || ''} ${row?.last_name || ''}`);
  const email = toTextValue(row?.email);

  if (fullName !== '' && email !== '') {
    return `${fullName} (${email})`;
  }

  return fullName || email || 'Persona de contacto';
};

const isBillingLocked = (row = {}) => {
  const candidates = [
    row?.used,
    row?.used_in_expenses,
    row?.has_expenses,
    row?.expenses_count,
  ];

  return candidates.some((value) => Number(value || 0) > 0);
};

export default {
  name: 'SupplierMergeModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    primarySupplier: {
      type: Object,
      default: null,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'submit'],
  data() {
    return {
      text,
      loadingCandidates: false,
      loadingMergeData: false,
      mergeCandidates: [],
      selectedMergeSupplierId: 0,
      mergeSupplierDetail: null,
      informationRows: [],
      billingRows: [],
      contactsRows: [],
      primarySummary: {
        expenses: 0,
        commissions: 0,
        payments: 0,
      },
      mergeSummary: {
        expenses: 0,
        commissions: 0,
        payments: 0,
      },
      confirmationValue: '',
    };
  },
  computed: {
    primarySupplierId() {
      return Number(this.primarySupplier?.id || 0);
    },
    primarySupplierName() {
      const value = toTextValue(this.primarySupplier?.name);
      return value !== '' ? value : this.text.common.noName;
    },
    mergeSupplierName() {
      const value = toTextValue(this.mergeSupplierDetail?.name || this.selectedMergeSupplier?.name);
      return value !== '' ? value : this.text.detail.mergeModal.mergeSupplierFallback;
    },
    selectedMergeSupplier: {
      get() {
        return this.mergeSupplierOptions.find((row) => Number(row?.id || 0) === this.selectedMergeSupplierId) || null;
      },
      set(option) {
        this.selectedMergeSupplierId = Number(option?.id || 0);
      },
    },
    mergeSupplierId() {
      return Number(this.selectedMergeSupplierId || 0);
    },
    mergeSupplierOptions() {
      return this.mergeCandidates.filter((row) => Number(row?.id || 0) > 0 && Number(row?.id || 0) !== this.primarySupplierId);
    },
    showInformationTable() {
      return this.mergeSupplierId > 0 && this.informationRows.length > 0;
    },
    showMergeDataSections() {
      return this.mergeSupplierId > 0;
    },
    relatedSummaryRows() {
      const rows = [
        { key: 'expenses', label: this.text.detail.mergeModal.summaryLabels.expenses },
        { key: 'commissions', label: this.text.detail.mergeModal.summaryLabels.commissions },
        { key: 'payments', label: this.text.detail.mergeModal.summaryLabels.payments },
      ];

      return rows.map((row) => {
        const primary = Number(this.primarySummary?.[row.key] || 0);
        const merge = Number(this.mergeSummary?.[row.key] || 0);

        return {
          ...row,
          primary,
          merge,
          total: primary + merge,
        };
      });
    },
    canSubmitMerge() {
      return this.mergeSupplierId > 0 && toTextValue(this.confirmationValue).toLowerCase() === 'fusionar';
    },
  },
  watch: {
    show(value) {
      if (value) {
        this.handleModalOpen();
      }
    },
    selectedMergeSupplierId() {
      this.loadMergeData();
    },
  },
  methods: {
    hasValue(value) {
      return hasContent(value);
    },
    resolveFieldValue(supplier, key) {
      if (key === 'status') {
        return Number(supplier?.active || 0) === 1 ? this.text.detail.status.active : this.text.detail.status.inactive;
      }

      if (key === 'class') {
        return toTextValue(supplier?.class?.name || supplier?.class_id);
      }

      if (key === 'category') {
        return toTextValue(supplier?.category?.name || supplier?.category_id);
      }

      if (key === 'area') {
        return toTextValue(supplier?.area?.name || supplier?.area_id);
      }

      if (key === 'comment') {
        return toTextValue(supplier?.comments || supplier?.comment);
      }

      if (key === 'zipCode') {
        return toTextValue(supplier?.zipcode || supplier?.zipCode);
      }

      if (key === 'accountingAccount') {
        return toTextValue(supplier?.reference || supplier?.accountingAccount);
      }

      return toTextValue(supplier?.[key]);
    },
    buildInformationFields() {
      return [
        { key: 'name', label: this.text.modal.form.general.fields.name, sourceKey: 'name' },
        { key: 'website', label: this.text.modal.form.general.fields.website, sourceKey: 'website' },
        { key: 'status', label: this.text.modal.form.general.fields.status, sourceKey: 'active' },
        { key: 'comment', label: this.text.modal.form.general.fields.comment, sourceKey: 'comments' },
        { key: 'email', label: this.text.modal.form.contact.fields.email, sourceKey: 'email' },
        { key: 'phone', label: this.text.modal.form.contact.fields.phone, sourceKey: 'phone' },
        { key: 'portfolio', label: this.text.modal.form.contact.fields.portfolio, sourceKey: 'portfolio' },
        { key: 'class', label: 'Clase', sourceKey: 'class' },
        { key: 'category', label: 'Categoría', sourceKey: 'category' },
        { key: 'area', label: this.text.modal.form.classification.fields.area, sourceKey: 'area' },
        { key: 'address', label: this.text.modal.form.address.fields.address, sourceKey: 'address' },
        { key: 'country', label: this.text.modal.form.address.fields.country, sourceKey: 'country' },
        { key: 'state', label: this.text.modal.form.address.fields.state, sourceKey: 'state' },
        { key: 'city', label: this.text.modal.form.address.fields.city, sourceKey: 'city' },
        { key: 'zipCode', label: this.text.modal.form.address.fields.zipCode, sourceKey: 'zipcode' },
      ];
    },
    buildInformationRows() {
      const fields = this.buildInformationFields();

      return fields.map((field) => {
        const primaryValue = this.resolveFieldValue(this.primarySupplier || {}, field.sourceKey || field.key);
        const mergeValue = this.resolveFieldValue(this.mergeSupplierDetail || {}, field.sourceKey || field.key);

        return {
          key: field.key,
          label: field.label,
          primaryValue,
          mergeValue,
          source: !hasContent(primaryValue) && hasContent(mergeValue) ? 'merge' : 'primary',
        };
      });
    },
    async handleModalOpen() {
      this.confirmationValue = '';
      this.selectedMergeSupplierId = 0;
      this.mergeSupplierDetail = null;
      this.informationRows = [];
      this.billingRows = [];
      this.contactsRows = [];
      this.mergeSummary = {
        expenses: 0,
        commissions: 0,
        payments: 0,
      };

      await Promise.all([
        this.loadMergeCandidates(),
        this.loadSummaryForPrimary(),
      ]);
    },
    async loadMergeCandidates() {
      this.loadingCandidates = true;

      try {
        const response = await suppliersService.selectors();
        this.mergeCandidates = extractCollection(response);
      } catch (error) {
        this.mergeCandidates = [];
      } finally {
        this.loadingCandidates = false;
      }
    },
    async loadSummaryForPrimary() {
      if (this.primarySupplierId <= 0) {
        this.primarySummary = {
          expenses: 0,
          commissions: 0,
          payments: 0,
        };
        return;
      }

      this.primarySummary = await this.resolveSupplierSummary(this.primarySupplierId);
    },
    async loadMergeData() {
      if (this.mergeSupplierId <= 0) {
        this.mergeSupplierDetail = null;
        this.informationRows = [];
        this.billingRows = [];
        this.contactsRows = [];
        this.mergeSummary = {
          expenses: 0,
          commissions: 0,
          payments: 0,
        };
        return;
      }

      this.loadingMergeData = true;

      try {
        const [detailResponse, contributorsResponse, personsResponse, summary] = await Promise.all([
          suppliersService.show(this.mergeSupplierId),
          suppliersService.contributorsIndex(this.mergeSupplierId),
          suppliersService.personsIndex(this.mergeSupplierId),
          this.resolveSupplierSummary(this.mergeSupplierId),
        ]);

        this.mergeSupplierDetail = extractPayload(detailResponse);
        this.informationRows = this.buildInformationRows();
        this.mergeSummary = summary;

        const contributors = extractCollection(contributorsResponse);
        this.billingRows = contributors.map((row) => ({
          key: `merge-${Number(row?.id || 0)}`,
          sourceId: Number(row?.id || 0),
          sourceType: 'merge',
          checked: true,
          locked: isBillingLocked(row),
          label: buildBillingLabel(row),
          originLabel: this.mergeSupplierName,
        }));

        const persons = extractCollection(personsResponse);
        this.contactsRows = persons.map((row) => ({
          key: `merge-${Number(row?.id || 0)}`,
          sourceId: Number(row?.id || 0),
          sourceType: 'merge',
          checked: true,
          label: buildContactLabel(row),
          originLabel: this.mergeSupplierName,
        }));
      } catch (error) {
        this.mergeSupplierDetail = null;
        this.informationRows = [];
        this.billingRows = [];
        this.contactsRows = [];
        this.mergeSummary = {
          expenses: 0,
          commissions: 0,
          payments: 0,
        };
      } finally {
        this.loadingMergeData = false;
      }
    },
    async resolveSupplierSummary(supplierId) {
      const id = Number(supplierId || 0);
      if (id <= 0) {
        return {
          expenses: 0,
          commissions: 0,
          payments: 0,
        };
      }

      const [expensesResult, commissionsResult, summaryResult] = await Promise.allSettled([
        expensesService.index({ provider_id: id }, { skipLoading: true }),
        feesService.index({ provider_id: id, commission_type: 'supplier' }, { skipLoading: true }),
        suppliersService.mergeSummary(id),
      ]);

      const expenses = expensesResult.status === 'fulfilled' ? extractCollection(expensesResult.value) : [];
      const commissions = commissionsResult.status === 'fulfilled' ? extractCollection(commissionsResult.value) : [];
      const summaryPayload = summaryResult.status === 'fulfilled' ? extractPayload(summaryResult.value) : {};

      return {
        expenses: Array.isArray(expenses) ? expenses.length : 0,
        commissions: Array.isArray(commissions) ? commissions.length : 0,
        payments: Number(summaryPayload?.payments || 0),
      };
    },
    selectInformationSource(fieldKey, source) {
      this.informationRows = this.informationRows.map((row) => {
        if (row.key !== fieldKey) {
          return row;
        }

        if (source === 'merge' && !hasContent(row.mergeValue) && hasContent(row.primaryValue)) {
          return row;
        }

        if (source === 'primary' && !hasContent(row.primaryValue) && hasContent(row.mergeValue)) {
          return row;
        }

        return {
          ...row,
          source,
        };
      });
    },
    toggleBillingRow(rowKey) {
      this.billingRows = this.billingRows.map((row) => {
        if (row.key !== rowKey || row.locked) {
          return row;
        }

        return {
          ...row,
          checked: !row.checked,
        };
      });
    },
    toggleContactRow(rowKey) {
      this.contactsRows = this.contactsRows.map((row) => {
        if (row.key !== rowKey) {
          return row;
        }

        return {
          ...row,
          checked: !row.checked,
        };
      });
    },
    emitClose() {
      if (this.submitting) {
        return;
      }

      this.$emit('close');
    },
    emitSubmit() {
      if (!this.canSubmitMerge || this.submitting) {
        return;
      }

      const payload = {
        primary_supplier_id: this.primarySupplierId,
        merge_supplier_id: this.mergeSupplierId,
        information_source: this.informationRows.reduce((carry, row) => {
          carry[row.key] = row.source;
          return carry;
        }, {}),
        contributors: this.billingRows
          .filter((row) => row.checked)
          .map((row) => ({
            id: row.sourceId,
            source: row.sourceType,
          })),
        contacts: this.contactsRows
          .filter((row) => row.checked)
          .map((row) => ({
            id: row.sourceId,
            source: row.sourceType,
          })),
      };

      this.$emit('submit', payload);
    },
  },
};
</script>

<style scoped>
.supplier-merge-modal__dialog {
  max-width: 1080px;
  height: auto;
  max-height: calc(100vh - 48px);
}

.supplier-merge-modal__content {
  min-height: auto;
}

.supplier-merge-modal__top-row {
  align-items: end;
}

.supplier-merge-modal__readonly {
  min-height: 38px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 8px;
  background: #f8fafc;
  padding: 8px 10px;
  color: #1e293b;
  display: flex;
  align-items: center;
}

.supplier-merge-modal__separator {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed rgba(148, 163, 184, 0.5);
}

.supplier-merge-modal__separator h4 {
  margin: 0;
  font-size: 15px;
  color: #0f172a;
}

.supplier-merge-modal__table-wrap {
  margin-top: 10px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 10px;
  overflow: hidden;
}

.supplier-merge-modal__table {
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
}

.supplier-merge-modal__table th,
.supplier-merge-modal__table td {
  border-bottom: 1px solid rgba(226, 232, 240, 0.9);
  padding: 9px 10px;
  font-size: 13px;
  vertical-align: top;
}

.supplier-merge-modal__table th {
  background: #f8fafc;
  color: #334155;
  font-weight: 700;
}

.supplier-merge-modal__table th:first-child,
.supplier-merge-modal__table td:first-child {
  width: 230px;
  min-width: 230px;
  max-width: 230px;
}

.supplier-merge-modal__choice {
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.supplier-merge-modal__checkbox {
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 1px solid rgba(100, 116, 139, 0.62);
  background: #ffffff;
  color: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  margin-top: 2px;
}

.supplier-merge-modal__checkbox i {
  font-size: 10px;
}

.supplier-merge-modal__checkbox.is-checked {
  border-color: #0070cd;
  background: #0070cd;
  color: #ffffff;
}

.supplier-merge-modal__checkbox.is-disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.supplier-merge-modal__checkbox--hidden {
  visibility: hidden;
  pointer-events: none;
}

.supplier-merge-modal__warning {
  margin-top: 10px;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.supplier-merge-modal__warning p {
  margin: 0;
  font-size: 12px;
  line-height: 18px;
}

.supplier-merge-modal__warning--soft {
  border: 1px solid rgba(14, 116, 144, 0.3);
  background: #ecfeff;
}

.supplier-merge-modal__warning--soft i,
.supplier-merge-modal__warning--soft p {
  color: #0e7490;
}

.supplier-merge-modal__warning--info {
  border: 1px solid rgba(30, 64, 175, 0.25);
  background: #eff6ff;
}

.supplier-merge-modal__warning--info i,
.supplier-merge-modal__warning--info p {
  color: #1d4ed8;
}

.supplier-merge-modal__list {
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.supplier-merge-modal__list-item {
  margin: 0;
  border: 1px solid rgba(148, 163, 184, 0.34);
  border-radius: 10px;
  padding: 9px 10px;
  background: #ffffff;
  display: grid;
  grid-template-columns: 18px 1fr;
  column-gap: 8px;
  row-gap: 2px;
  align-items: center;
}

.supplier-merge-modal__list-item--locked {
  background: #f8fafc;
  border-style: dashed;
}

.supplier-merge-modal__list-main {
  font-size: 13px;
  color: #0f172a;
}

.supplier-merge-modal__list-item small {
  color: #64748b;
  font-size: 11px;
}

.supplier-merge-modal__table--summary td:nth-child(n + 2) {
  text-align: center;
}

.supplier-merge-modal__table--summary {
  margin-top: 10px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 10px;
  border-collapse: separate;
  border-spacing: 0;
}

.supplier-merge-modal__separator--warning {
  margin-top: 14px;
}

.supplier-merge-modal__confirm {
  margin-top: 12px;
}

@media (max-width: 960px) {
  .supplier-merge-modal__list {
    grid-template-columns: 1fr;
  }

  .supplier-merge-modal__table-wrap {
    overflow-x: auto;
  }

  .supplier-merge-modal__table {
    min-width: 760px;
  }
}
</style>
